import React from "react";
import { Link } from "react-router-dom";
import { useSiteContent } from "../components/SiteGate";
import { Sparkles, ShieldCheck, Clock, Heart, BarChart3, Stethoscope, Users, CheckCircle2 } from "lucide-react";

const features = [
  { icon: Sparkles, title: "AI Progress Notes", desc: "SOAP, DAP, BIRP, PIE. Expands from bullets in seconds. Learns your voice." },
  { icon: ClockIcon, title: "Save 10+ Hours Weekly", desc: "Treatment plans, prior auths, referrals — drafted by AI, refined by you." },
  { icon: ShieldCheck, title: "HIPAA-Ready", desc: "AES-256 encryption, audit logs, 15-min auto-logout, BAA available." },
  { icon: Heart, title: "Stanley-Brown Safety Plans", desc: "AI-prefilled crisis planning. 30/60/90 day review reminders built-in." },
  { icon: BarChart3, title: "Outcomes Tracking", desc: "PHQ-9, GAD-7, PCL-5 auto-scored. Trend graphs. Funder-ready reports." },
  { icon: Stethoscope, title: "Split-Screen Telehealth", desc: "Video on the left, live note editor on the right. AI suggests content as you go." },
];

function ClockIcon(props) { return <Clock {...props} />; }

export default function Landing() {
  const c = useSiteContent();
  return (
    <div className="min-h-screen" style={{ background: "var(--mf-bg)" }}>
      {/* Nav */}
      <header className="px-6 md:px-10 py-5 flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "var(--mf-primary)" }}>
            <img src="/icons/icon-192.png" alt="Clinician Well logo" className="w-full h-full object-cover rounded-[inherit]" />
          </div>
          <span className="font-serif text-2xl" style={{ color: "var(--mf-primary)" }}>Clinician Well</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm font-medium hidden sm:inline" style={{ color: "var(--mf-text)" }} data-testid="landing-login-link">Sign In</Link>
          <Link to="/register" className="mf-btn-primary" data-testid="landing-signup-btn">Get Started</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-20 pb-16 grid md:grid-cols-2 gap-10 items-center mf-noise">
        <div className="mf-fade-in">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs mb-6" style={{ background: "rgba(201,168,76,0.18)", color: "#8a6f20" }}>
            <Sparkles className="w-3.5 h-3.5" />
            {c.hero_badge}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-5" style={{ color: "var(--mf-navy, #0F1B2D)" }}>
            {c.hero_title_1}
            <br />
            <span style={{ color: "var(--mf-primary)" }}>{c.hero_title_2}</span>
            <br />
            {c.hero_title_3}
          </h1>
          <p className="text-lg leading-relaxed mb-7 max-w-xl" style={{ color: "var(--mf-text-muted)" }}>
            {c.hero_subtitle}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/register" className="mf-btn-primary" data-testid="hero-start-btn">{c.hero_cta_primary}</Link>
            <Link to="/login" className="px-6 py-2.5 rounded-full font-medium border" style={{ borderColor: "var(--mf-primary)", color: "var(--mf-primary)" }} data-testid="hero-login-btn">{c.hero_cta_secondary}</Link>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-5 text-sm" style={{ color: "var(--mf-text-muted)" }}>
            <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" style={{ color: "var(--mf-secondary)" }} /> HIPAA-ready</div>
            <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" style={{ color: "var(--mf-secondary)" }} /> 5-minute setup</div>
            <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" style={{ color: "var(--mf-secondary)" }} /> No credit card required</div>
          </div>
        </div>
        <div className="mf-fade-in mf-delay-200 relative">
          <div className="mf-card p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 -mt-20 -mr-20" style={{ background: "var(--mf-primary)" }}></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "var(--mf-primary)" }}>
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="font-serif text-lg">Sage AI</div>
                  <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Writing SOAP note…</div>
                </div>
              </div>
              <div className="space-y-3 text-sm mf-prose">
                <div className="p-3 rounded-xl" style={{ background: "var(--mf-muted)" }}>
                  <div className="text-xs mb-1" style={{ color: "var(--mf-text-muted)" }}>Your input</div>
                  <div>"45-min session. Client reported sleep improvement with the new coping strategy. Anxiety 4/10 vs 8/10 last week."</div>
                </div>
                <div className="p-4 rounded-xl border" style={{ borderColor: "var(--mf-secondary)", background: "rgba(123,174,127,0.08)" }}>
                  <div className="text-xs mb-2" style={{ color: "var(--mf-secondary)" }}>AI-Generated SOAP</div>
                  <div style={{ color: "var(--mf-text)" }}><strong>S:</strong> Client reports improved sleep duration (6h from 3h) attributing gain to progressive muscle relaxation introduced previous session…</div>
                  <div className="mt-2" style={{ color: "var(--mf-text)" }}><strong>O:</strong> Eye contact sustained; affect congruent; anxiety self-rated 4/10 (reduced from 8/10)…</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="max-w-2xl mb-12 mf-fade-in">
          <div className="text-sm uppercase tracking-wider mb-2" style={{ color: "var(--mf-accent)" }}>Everything you need</div>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Ten core modules. One calm platform.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div key={f.title} className="mf-card p-7 mf-fade-in" style={{ animationDelay: `${i * 0.08}s` }}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(26,107,114,0.1)" }}>
                <f.icon className="w-5 h-5" style={{ color: "var(--mf-primary)" }} strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-xl mb-2">{f.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--mf-text-muted)" }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="text-center mb-12 mf-fade-in">
          <div className="text-sm uppercase tracking-wider mb-2" style={{ color: "var(--mf-accent)" }}>Simple pricing</div>
          <h2 className="font-serif text-4xl sm:text-5xl">Built for every practice size</h2>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { name: "Solo Starter", price: "$59", clients: "1 clinician · 30 clients", features: ["All 10 modules", "AI notes", "Intake & scheduling"] },
            { name: "Practice Pro", price: "$129", clients: "3 clinicians · unlimited", features: ["Everything in Solo", "Telehealth", "Billing automation"], featured: true },
            { name: "Clinic Plus", price: "$299", clients: "10 clinicians · unlimited", features: ["Everything in Pro", "EAP portal", "Visual analytics"] },
            { name: "Enterprise", price: "Custom", clients: "Unlimited", features: ["White-label", "API + SSO", "Dedicated support"] },
          ].map((tier, i) => (
            <div
              key={tier.name}
              className="mf-card p-6 relative mf-fade-in"
              style={{
                animationDelay: `${i * 0.08}s`,
                ...(tier.featured ? { background: "var(--mf-primary)", color: "#FAFAF8" } : {})
              }}
            >
              {tier.featured && <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: "var(--mf-accent)", color: "#0F1B2D" }}>POPULAR</div>}
              <div className="font-serif text-xl mb-1">{tier.name}</div>
              <div className="font-serif text-4xl mb-1">{tier.price}{tier.price !== "Custom" && <span className="text-sm font-sans font-normal">/mo</span>}</div>
              <div className="text-xs mb-5 opacity-80">{tier.clients}</div>
              <ul className="space-y-2 text-sm">
                {tier.features.map((f) => <li key={f} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 shrink-0" />{f}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-20 text-center mf-fade-in">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ background: "var(--mf-primary)" }}>
          <Users className="w-7 h-7 text-white" strokeWidth={1.5} />
        </div>
        <h2 className="font-serif text-4xl sm:text-5xl mb-4">{c.final_title}</h2>
        <p className="text-lg mb-7 max-w-xl mx-auto" style={{ color: "var(--mf-text-muted)" }}>{c.final_subtitle}</p>
        <Link to="/register" className="mf-btn-primary" data-testid="cta-signup-btn">{c.final_cta}</Link>
      </section>

      <footer className="border-t py-8 text-center text-sm" style={{ borderColor: "var(--mf-muted)", color: "var(--mf-text-muted)" }}>
        © 2026 Clinician Well. Built with care for clinicians.
      </footer>
    </div>
  );
}
